import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback
} from 'react'
import { io } from 'socket.io-client'
import { useAuth } from './AuthContext'
import api from '../api/axiosInstance'

const SensorDataContext = createContext(null)

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_URL?.replace('/api', '') ||
  'http://localhost:5000'
const MAX_CHART_POINTS = 60

export const SensorDataProvider = ({ children }) => {
  const { user, activeTenant } = useAuth()

  const resolvedTenantId =
    (typeof activeTenant === 'object' ? activeTenant?.id : activeTenant) ??
    user?.tenant_id

  // "connecting" | "connected" | "disconnected" | "error"
  const [socketStatus, setSocketStatus] = useState('disconnected')

  // { [deviceId]: { temperature, vibration, ..., status, statusLabel, isOnline, recordedAt } }
  const [liveData, setLiveData] = useState({})

  // { [controllerId]: { controllerId, controllerName, deviceId, status, statusLabel, isOnline, receivedAt, payload } }
  const [livePayloads, setLivePayloads] = useState({})

  const clearLivePayload = useCallback(controllerId => {
    if (!controllerId) return
    setLivePayloads(prev => {
      const next = { ...prev }
      delete next[controllerId]
      return next
    })
  }, [])

  // { [deviceId]: [{ time, temperature, ... }] }
  const [chartData, setChartData] = useState({})

  const clearLiveData = useCallback(deviceId => {
    if (!deviceId) return
    setLiveData(prev => {
      const next = { ...prev }
      delete next[deviceId]
      return next
    })
  }, [])

  // Force fetch live payload for a controller
  const fetchLivePayload = useCallback(async (controllerId, tenantId) => {
    if (!controllerId || !tenantId) return
    try {
      const res = await api.get(
        `/iot-controllers/${controllerId}/live-payload`,
        {
          headers: { 'X-Tenant-Id': tenantId }
        }
      )
      const data = res.data?.data || res.data
      if (data) {
        setLivePayloads(prev => ({ ...prev, [controllerId]: data }))
      }
    } catch (err) {
      console.warn('[SensorData] Failed to fetch live payload:', err)
    }
  }, [])

  // { [controllerId]: [{ time, p1, p2, ... }] }  — built from live_payload Data.*
  const [dynamicChartData, setDynamicChartData] = useState({})

  // { [controllerId]: { p1: { display_name, unit }, ... } }
  const [paramMappingsCache, setParamMappingsCache] = useState({})

  const socketRef = useRef(null)

  // ── sensor_data handler (normalised fields) ────────────────────────────────
  const handleSensorData = useCallback(data => {
    const sn = data.deviceId
    if (!sn) return

    const reading = {
      temperature: data.temperature ?? null,
      vibration: data.vibration ?? null,
      current: data.current ?? null,
      pressure: data.pressure ?? null,
      humidity: data.humidity ?? null,
      rpm: data.rpm ?? null,
      status: data.status ?? 'online',
      statusLabel: data.statusLabel ?? data.status ?? 'Online',
      isOnline: data.isOnline ?? true,
      controllerName: data.controllerName ?? sn,
      recordedAt: data.recordedAt ?? new Date().toISOString()
    }

    setLiveData(prev => ({ ...prev, [sn]: reading }))

    const point = {
      time: new Date(reading.recordedAt).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      temperature: reading.temperature,
      vibration: reading.vibration,
      current: reading.current,
      pressure: reading.pressure,
      humidity: reading.humidity,
      rpm: reading.rpm
    }

    setChartData(prev => {
      const existing = prev[sn] || []
      return {
        ...prev,
        [sn]: [...existing.slice(-(MAX_CHART_POINTS - 1)), point]
      }
    })
  }, [])

  // ── live_payload handler (full raw payload for dashboard cards) ────────────
  const handleLivePayload = useCallback(data => {
    const cid = data.controllerId
    if (!cid) return

    setLivePayloads(prev => ({ ...prev, [cid]: data }))

    // Cache param mappings whenever they arrive
    if (data.paramMappings && Object.keys(data.paramMappings).length > 0) {
      setParamMappingsCache(prev => ({ ...prev, [cid]: data.paramMappings }))
    }

    // Build dynamic chart point from Data.* fields (p1, p2, …)
    const dataObj = data.payload?.Data || {}
    if (Object.keys(dataObj).length > 0) {
      const point = {
        time: new Date(data.receivedAt || Date.now()).toLocaleTimeString(
          'en-GB',
          {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }
        ),
        ...dataObj
      }
      setDynamicChartData(prev => {
        const existing = prev[cid] || []
        return {
          ...prev,
          [cid]: [...existing.slice(-(MAX_CHART_POINTS - 1)), point]
        }
      })
    }
  }, [])

  // ── Load param mappings via REST API and populate cache ───────────────────
  const loadParamMappings = useCallback(
    async controllers => {
      if (!controllers?.length || !resolvedTenantId) return
      await Promise.allSettled(
        controllers.map(async ctrl => {
          try {
            const res = await api.get(`/iot-controllers/${ctrl.id}/params`, {
              headers: { 'X-Tenant-Id': resolvedTenantId }
            })
            const list = res.data?.data || res.data || []
            if (!Array.isArray(list) || !list.length) return
            const byKey = Object.fromEntries(
              list.map(m => [
                m.param_key,
                {
                  display_name: m.display_name || m.label || m.param_key,
                  unit: m.unit || m.resolved_unit || '',
                  min_value: m.min_value ?? null,
                  max_value: m.max_value ?? null,
                }
              ])
            )
            setParamMappingsCache(prev => ({ ...prev, [ctrl.id]: byKey }))
          } catch {
            // ignore per-controller failures silently
          }
        })
      )
    },
    [resolvedTenantId]
  )

  // ── Load historical data from REST API ─────────────────────────────────────
  const loadHistoricalData = useCallback(
    async controllers => {
      if (!controllers?.length || !resolvedTenantId) return

      try {
        const tenantHeader = { headers: { 'X-Tenant-Id': resolvedTenantId } }

        await Promise.allSettled(
          controllers.map(async ctrl => {
            const res = await api.get(
              `/sensor-data/${ctrl.id}/recent?limit=60`,
              tenantHeader
            )
            const readings = res.data?.data || res.data || []
            if (!readings.length) return

            const points = readings.map(r => ({
              time: new Date(r.recorded_at || r.time).toLocaleTimeString(
                'en-GB',
                {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                }
              ),
              temperature: r.temperature,
              vibration: r.vibration,
              current: r.current,
              pressure: r.pressure,
              humidity: r.humidity,
              rpm: r.rpm
            }))

            const deviceId = ctrl.device_id
            if (!deviceId) return
            setChartData(prev => ({ ...prev, [deviceId]: points }))

            const latest = readings[readings.length - 1]
            if (latest) {
              setLiveData(prev => ({
                ...prev,
                [deviceId]: {
                  temperature: latest.temperature,
                  vibration: latest.vibration,
                  current: latest.current,
                  pressure: latest.pressure,
                  humidity: latest.humidity,
                  rpm: latest.rpm,
                  status: ctrl.status,
                  statusLabel: ctrl.status_label || ctrl.status,
                  isOnline: ctrl.is_online ?? false,
                  recordedAt: latest.recorded_at || latest.time
                }
              }))
            }
          })
        )
      } catch (err) {
        console.warn('[SensorData] Failed to load historical data:', err)
      }
    },
    [resolvedTenantId]
  )

  // ── Socket.IO connection lifecycle ─────────────────────────────────────────
  useEffect(() => {
    if (!user || !resolvedTenantId) return

    if (socketRef.current) {
      socketRef.current.off('sensor_data', handleSensorData)
      socketRef.current.off('live_payload', handleLivePayload)
      socketRef.current.disconnect()
      socketRef.current = null
    }

    setSocketStatus('connecting')

    const socket = io(`${SOCKET_URL}/sensors`, {
      auth: { tenantId: resolvedTenantId },
      transports: ['websocket'],
      reconnectionDelay: 3000,
      reconnectionDelayMax: 10000
    })

    socketRef.current = socket

    socket.on('connect', () => setSocketStatus('connected'))
    socket.on('disconnect', () => setSocketStatus('disconnected'))
    socket.on('connect_error', e => {
      console.error('[SocketIO] Connect error:', e.message)
      setSocketStatus('error')
    })
    socket.on('sensor_data', handleSensorData)
    socket.on('live_payload', handleLivePayload)

    return () => {
      socket.off('sensor_data', handleSensorData)
      socket.off('live_payload', handleLivePayload)
      socket.disconnect()
      socketRef.current = null
    }
  }, [user, resolvedTenantId, handleSensorData, handleLivePayload])

  return (
    <SensorDataContext.Provider
      value={{
        socketStatus,
        liveData,
        livePayloads,
        chartData,
        dynamicChartData,
        paramMappingsCache,
        loadHistoricalData,
        loadParamMappings,
        setChartData,
        clearLivePayload,
        clearLiveData,
        fetchLivePayload
      }}
    >
      {children}
    </SensorDataContext.Provider>
  )
}

export const useSensorData = () => {
  const ctx = useContext(SensorDataContext)
  if (!ctx)
    throw new Error('useSensorData must be used inside <SensorDataProvider>')
  return ctx
}
